import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { bookUrl } from "@/lib/seo";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Truck, Info } from "lucide-react";

export default function Cart() {
  const { cart, updateQty, removeItem, loading } = useCart();
  const { user } = useAuth();
  const { currency, symbol, unitPrice, unitMrp, isIndia } = useCurrency();
  const navigate = useNavigate();

  // Old inline early return removed (handled above)

  if (loading) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="cart-loading">
      <div className="inline-block w-8 h-8 border-4 border-[#e4e4e7] border-t-[#ea580c] rounded-full animate-spin" />
      <div className="text-[#52525b] mt-4">Loading your cart…</div>
    </div>
  );

  const sym = symbol();
  const totalInCur = cart.items.reduce((s, it) => s + unitPrice(it.book) * it.quantity, 0);
  const mrpTotal = cart.items.reduce((s, it) => {
    const m = unitMrp(it.book);
    return s + (m > 0 ? m : unitPrice(it.book)) * it.quantity;
  }, 0);
  const savings = Math.max(0, mrpTotal - totalInCur);
  const itemCount = cart.items.reduce((s, it) => s + it.quantity, 0);

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="cart-page">
        <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-[#a1a1aa]" />
        <h1 className="font-display text-3xl font-black mb-2">Your cart is empty</h1>
        <p className="text-[#52525b] mb-6">Add some books to get started.</p>
        <Link to="/books" className="inline-block bg-[#09090b] text-white font-bold px-6 py-3">Browse Books</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="cart-page">
      <h1 className="font-display text-3xl md:text-5xl font-black border-b-2 border-[#09090b] pb-4 mb-6">Your Cart</h1>
      {!user && (
        <div className="mb-4 bg-[#fff7ed] border-2 border-[#ea580c] px-4 py-3 flex flex-wrap items-center justify-between gap-2" data-testid="guest-signin-nudge">
          <div className="text-sm">
            <span className="font-bold">Guest checkout enabled</span>
            <span className="text-[#52525b]"> — or <Link to="/login" className="text-[#ea580c] underline font-bold">sign in</Link> for faster checkout &amp; order tracking.</span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 border border-border">
          {cart.items.map((it) => (
            <div key={it.book.id} className="flex gap-4 p-4 border-b border-border last:border-b-0" data-testid={`cart-item-${it.book.id}`}>
              <Link to={bookUrl(it.book)} className="w-20 h-28 bg-[#f4f4f5] border border-border shrink-0 overflow-hidden">
                {it.book.image_url && <img src={it.book.image_url} alt={it.book.title} className="w-full h-full object-cover" />}
              </Link>
              <div className="flex-1 flex flex-col">
                <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#ea580c]">{it.book.publisher}</div>
                <Link to={bookUrl(it.book)} className="font-display font-bold hover:underline">{it.book.title}</Link>
                <div className="text-xs text-[#52525b]">{it.book.author}</div>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex flex-col items-start">
                    <div className="flex border border-[#09090b]">
                      <button onClick={() => updateQty(it.book.id, it.quantity - 1)} className="px-2 py-1 hover:bg-[#f4f4f5]" data-testid={`cart-dec-${it.book.id}`}><Minus className="w-3 h-3" /></button>
                      <div className="px-3 py-1 text-sm font-bold">{it.quantity}</div>
                      <button
                        onClick={() => updateQty(it.book.id, it.quantity + 1)}
                        disabled={typeof it.book.stock === "number" && it.quantity >= it.book.stock}
                        className="px-2 py-1 hover:bg-[#f4f4f5] disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`cart-inc-${it.book.id}`}
                        title={typeof it.book.stock === "number" && it.quantity >= it.book.stock ? `Only ${it.book.stock} available` : "Add one"}
                      ><Plus className="w-3 h-3" /></button>
                    </div>
                    {typeof it.book.stock === "number" && (
                      <div className={`mt-1 text-[10px] ${it.quantity >= it.book.stock ? 'text-[#dc2626] font-bold' : 'text-[#71717a]'}`}>
                        {it.book.stock <= 0 ? "Out of stock" : `Only ${it.book.stock} in stock`}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    {(() => {
                      const p = unitPrice(it.book);
                      const m = unitMrp(it.book);
                      const line = p * it.quantity;
                      const lineMrp = m > 0 ? m * it.quantity : 0;
                      const disc = lineMrp > line && lineMrp > 0 ? Math.round(((lineMrp - line) / lineMrp) * 100) : 0;
                      return (
                        <>
                          <div className="font-display font-black text-lg" data-testid={`cart-item-price-${it.book.id}`}>{sym}{isIndia ? Math.round(line) : line.toFixed(2)}</div>
                          {lineMrp > line && (
                            <div className="flex items-baseline justify-end gap-2 text-xs mt-0.5">
                              <span className="line-through text-[#a1a1aa]">{sym}{isIndia ? Math.round(lineMrp) : lineMrp.toFixed(2)}</span>
                              {disc > 0 && <span className="text-[#16a34a] font-bold">{disc}% off</span>}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <button onClick={() => removeItem(it.book.id)} className="text-[#a1a1aa] hover:text-[#dc2626]" data-testid={`cart-remove-${it.book.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <aside className="lg:col-span-4 border-2 border-[#09090b] p-6 h-fit sticky top-32">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-4">Order Summary · {currency}</div>
          {/* International shipping calculator (non-India only) */}
          {!isIndia && (() => {
            const shippingCost = totalInCur < 150 ? 10 : 0;
            const grandTotal = totalInCur + shippingCost;
            const needed = Math.max(0, 150 - totalInCur);
            return (
              <div className="space-y-2 text-sm" data-testid="cart-intl-summary">
                <div className="flex justify-between">
                  <div className="text-[#52525b]">Price ({itemCount} {itemCount === 1 ? "Book" : "Books"})</div>
                  <div className="font-semibold">{sym}{mrpTotal.toFixed(2)}</div>
                </div>
                {savings > 0 && (
                  <div className="flex justify-between">
                    <div className="text-[#52525b]">Discount</div>
                    <div className="font-semibold text-[#16a34a]">– {sym}{savings.toFixed(2)}</div>
                  </div>
                )}
                <Row
                  label={<span className="flex items-center gap-1"><Truck className="w-3 h-3" />Shipping</span>}
                  value={shippingCost === 0 ? <span className="text-[#16a34a] font-bold">FREE</span> : `${sym}${shippingCost.toFixed(2)}`}
                />
                {needed > 0 && (
                  <div className="text-[10px] text-[#7c2d12] bg-[#fff7ed] border border-[#ea580c] p-2 leading-snug" data-testid="cart-free-shipping-hint">
                    Add <b>{sym}{needed.toFixed(2)}</b> more to unlock <b>FREE international shipping</b> (at ${150}+).
                  </div>
                )}
                <div className="border-t border-border pt-3 flex items-baseline justify-between">
                  <div className="font-display font-black text-lg">Amount Payable</div>
                  <div className="font-display font-black text-2xl" data-testid="cart-total">{sym}{grandTotal.toFixed(2)}</div>
                </div>
                {savings > 0 && (
                  <div className="bg-[#dcfce7] border border-[#16a34a] p-2.5 text-center text-[#166534] text-sm font-bold" data-testid="cart-total-savings-intl">
                    Total saving on this order is {sym}{savings.toFixed(2)}
                  </div>
                )}
              </div>
            );
          })()}
          {isIndia && (
            <div className="space-y-2 text-sm" data-testid="cart-india-summary">
              <div className="flex justify-between">
                <div className="text-[#52525b]">Price ({itemCount} {itemCount === 1 ? "Book" : "Books"})</div>
                <div className="font-semibold" data-testid="cart-mrp-total">{sym}{Math.round(mrpTotal)}</div>
              </div>
              {savings > 0 && (
                <div className="flex justify-between">
                  <div className="text-[#52525b]">Discount</div>
                  <div className="font-semibold text-[#16a34a]" data-testid="cart-discount">– {sym}{Math.round(savings)}</div>
                </div>
              )}
              <div className="flex justify-between text-[#52525b] text-xs">
                <div>Shipping</div>
                <div>Calculated at checkout</div>
              </div>
              <div className="border-t border-border pt-3 flex items-baseline justify-between">
                <div className="font-display font-black text-lg">Amount Payable</div>
                <div className="font-display font-black text-2xl" data-testid="cart-total">{sym}{Math.round(totalInCur)}</div>
              </div>
              {savings > 0 && (
                <div className="bg-[#dcfce7] border border-[#16a34a] p-2.5 text-center text-[#166534] text-sm font-bold" data-testid="cart-total-savings">
                  Total saving on this order is {sym}{Math.round(savings)}
                </div>
              )}
            </div>
          )}
          <button
            onClick={() => navigate("/checkout")}
            className="mt-6 w-full bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold py-3 flex items-center justify-center gap-2"
            data-testid="proceed-to-checkout"
          >
            Checkout <ArrowRight className="w-4 h-4" />
          </button>
          {!isIndia && (
            <div className="mt-4 text-[10px] text-[#52525b] flex gap-1.5 leading-relaxed">
              <Info className="w-3 h-3 shrink-0 mt-0.5 text-[#ea580c]" />
              <span>International orders may be subject to <b>customs duties &amp; taxes</b>, payable by the buyer upon delivery.</span>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-[#52525b]">{label}</div>
      <div className={muted ? "text-[#a1a1aa] text-xs" : "font-semibold"}>{value}</div>
    </div>
  );
}
